import { useState, useEffect, useRef } from "react";
import { Alert, Box, TextField, styled, CircularProgress } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CollapsibleSection from "./CollapsibleSection";
import { useCheckoutSimplifiedUI } from "../../CheckoutSimplifiedUIProvider";

const FormContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "0",
  paddingBottom: "8px",
});

interface ContactSectionProps {
  isOpen: boolean;
  onToggle: () => void;
  onComplete: () => void;
}

export const ContactSection = ({
  isOpen,
  onToggle,
  onComplete,
}: ContactSectionProps) => {
  const { session, updateBuyer, updatingBuyer, setContactHasErrors } =
    useCheckoutSimplifiedUI();

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>("");
  const [touched, setTouched] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [backendError, setBackendError] = useState<string | null>(null);
  const isResettingRef = useRef(false);
  const successTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const fadeOutTimeoutRef = useRef<NodeJS.Timeout>(undefined);

  // Initialize email from session buyer info
  useEffect(() => {
    if (session?.buyer?.email) {
      setEmail(session.buyer.email);
    }
  }, [session?.buyer?.email]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (fadeOutTimeoutRef.current) {
        clearTimeout(fadeOutTimeoutRef.current);
      }
    };
  }, []);

  // Clear backend error when user modifies the email
  useEffect(() => {
    if (backendError) {
      setBackendError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  // Report validation state to context
  useEffect(() => {
    setContactHasErrors(!!(error || backendError));
  }, [error, backendError, setContactHasErrors]);

  const validateEmail = (value: string): string => {
    if (!value.trim()) return "Email is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return "Invalid email address";
    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);

    if (touched) {
      setError(validateEmail(value));
    }
  };

  const handleBlur = async () => {
    // Skip validation if we're resetting via Escape
    if (isResettingRef.current) {
      isResettingRef.current = false;
      return;
    }

    setTouched(true);
    const validationError = validateEmail(email);
    setError(validationError);

    // Only save if email is valid AND has changed from session value
    if (!validationError && email.trim() && email !== session?.buyer?.email) {
      try {
        await updateBuyer({
          first_name: session?.buyer?.first_name || "",
          last_name: session?.buyer?.last_name || "",
          email: email,
          phone_number: session?.buyer?.phone_number,
        });
        setBackendError(null);
        onComplete();

        // Show success checkmark
        setShowSuccess(true);
        setIsFadingOut(false);

        // Clear any existing timeouts
        if (successTimeoutRef.current) {
          clearTimeout(successTimeoutRef.current);
        }
        if (fadeOutTimeoutRef.current) {
          clearTimeout(fadeOutTimeoutRef.current);
        }

        // Start fade out after 2 seconds
        successTimeoutRef.current = setTimeout(() => {
          setIsFadingOut(true);

          // Remove from DOM after fade completes
          fadeOutTimeoutRef.current = setTimeout(() => {
            setShowSuccess(false);
            setIsFadingOut(false);
          }, 500); // Match the transition duration
        }, 2000);
      } catch (err: any) {
        console.error("Failed to update buyer:", err);
        setBackendError(err?.message || "Failed to update contact information");
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      // Revert to original session value
      const originalEmail = session?.buyer?.email || "";
      setEmail(originalEmail);
      setError("");
      setTouched(false);

      // Set flag to skip blur validation
      isResettingRef.current = true;

      // Blur the input to exit edit mode
      (e.target as HTMLInputElement).blur();
    } else if (e.key === "Enter") {
      // Trigger blur to validate and save
      e.preventDefault(); // Prevent form submission
      (e.target as HTMLInputElement).blur();
    }
  };

  return (
    <CollapsibleSection
      isOpen={isOpen}
      onToggle={onToggle}
      disabled={false}
      title="Contact"
      titleIcon={
        updatingBuyer ? (
          <CircularProgress
            size={16}
            thickness={4}
            sx={{
              color: "text.secondary",
              opacity: 0.6,
            }}
          />
        ) : showSuccess ? (
          <CheckCircleIcon
            sx={{
              fontSize: 20,
              color: "success.main",
              opacity: isFadingOut ? 0 : 1,
              transform: isFadingOut ? "scale(0.8)" : "scale(1)",
              transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
              animation: isFadingOut ? "none" : "fadeIn 0.3s ease-in",
              "@keyframes fadeIn": {
                from: { opacity: 0, transform: "scale(0.8)" },
                to: { opacity: 1, transform: "scale(1)" },
              },
            }}
          />
        ) : null
      }
      hideControl={false}
    >
      <FormContainer>
        {backendError && (
          <Alert
            severity="error"
            onClose={() => setBackendError(null)}
            sx={{ marginBottom: 1 }}
          >
            {backendError}
          </Alert>
        )}
        <TextField
          fullWidth
          type="email"
          label="Email"
          value={email}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          error={touched && !!error}
          helperText={touched && error}
          disabled={updatingBuyer}
          required
        />
      </FormContainer>
    </CollapsibleSection>
  );
};
